import { ClientId, CommunityId, SentMessageType } from "@sp-crm/core";
import {
    ConversationFeedBasicsFragment,
    ConversationThreadType,
    SentMessageFragmentFragment,
} from "generated/graphql";

export type UnifiedMessageType = "sent" | "received" | "sms";

export interface UnifiedMessageIntakeContext {
    community?: {
        id: CommunityId;
        name: string;
        appLink: string;
    } | null;
    client?: {
        id: ClientId;
        name: string;
        bestContactName: string;
        appLink: string;
    } | null;
}

export interface UnifiedMessage {
    id: string;
    type: UnifiedMessageType;
    subject: string;
    timestamp: Date;
    fromDisplay: string;
    toDisplay: string;
    status?: string;
    latestEventStatus?: string | null;
    messageType?: SentMessageType | null;
    bodyPreview?: string;
    attachmentCount: number;
    openedCount: number;
    clickCount: number;
    intakeContext?: UnifiedMessageIntakeContext;
    originalSent?: SentMessageFragmentFragment;
    originalReceived?: ReceivedMessage;
    originalSmsThread?: ConversationFeedBasicsFragment["threads"][number]["smsThread"];
}

type ReceivedMessage = NonNullable<
    NonNullable<
        ConversationFeedBasicsFragment["threads"][number]["emailThread"]
    >["mailboxMessages"]
>[number];

export function formatContactDisplay(
    name: string | null | undefined,
    email: string | null | undefined,
): string {
    if (name && email && name !== email) {
        return `${name} <${email}>`;
    }
    return email || name || "";
}

export function convertReceivedMessage(message: ReceivedMessage): UnifiedMessage {
    const from = message.from;
    const fromDisplay = formatContactDisplay(from?.name, from?.email);

    const toDisplay = message.to
        .map(r => formatContactDisplay(r.contact.name, r.contact.email))
        .join(", ");

    return {
        id: message.id,
        type: "received",
        subject: message.subject,
        timestamp: new Date(message.receivedTimestamp),
        fromDisplay,
        toDisplay,
        bodyPreview: message.bodyPreview,
        attachmentCount: message.attachmentCount,
        openedCount: 0,
        clickCount: 0,
        originalReceived: message,
    };
}

export function convertSentMessage(message: SentMessageFragmentFragment): UnifiedMessage {
    const toDisplay = (message.to ?? []).join(", ");

    const openedCount = message.events
        ? message.events.filter(e => e.eventType === "opened").length
        : 0;

    const clickCount =
        message.clickedLinkEvents?.reduce(
            (acc, e) => acc + (e.eventsV2?.length || 0),
            0,
        ) || 0;

    const intakeContext: UnifiedMessageIntakeContext = {};
    if (message.intakeCommunity) {
        intakeContext.community = {
            id: message.intakeCommunity.id,
            name: message.intakeCommunity.name,
            appLink: message.intakeCommunity.appLink,
        };
    }
    if (message.intakeClient) {
        intakeContext.client = {
            id: message.intakeClient.id,
            name: message.intakeClient.name,
            bestContactName: message.intakeClient.bestContactName,
            appLink: message.intakeClient.appLink,
        };
    }

    return {
        id: message.id,
        type: "sent",
        subject: message.subject ?? "(no subject)",
        timestamp: new Date(message.createdAt),
        fromDisplay: "You",
        toDisplay,
        status: message.status,
        latestEventStatus: message.latestEventStatus,
        messageType: message.messageType as SentMessageType | null,
        attachmentCount: message.attachments?.length ?? 0,
        openedCount,
        clickCount,
        intakeContext:
            intakeContext.community || intakeContext.client ? intakeContext : undefined,
        originalSent: message,
    };
}

export function convertSmsThread(
    thread: ConversationFeedBasicsFragment["threads"][number],
): UnifiedMessage | null {
    if (!thread.smsThread) return null;

    const latestMessage = thread.smsThread.messages[0];
    if (!latestMessage) return null;

    const isOutbound = latestMessage.direction === "Outbound";

    return {
        id: thread.id,
        type: "sms",
        subject: thread.smsThread.messages[0]?.body ?? "(no content)",
        timestamp: new Date(thread.latestTimestamp),
        fromDisplay: isOutbound ? "You" : thread.smsThread.contact.phoneNumber,
        toDisplay: isOutbound ? thread.smsThread.contact.phoneNumber : "You",
        bodyPreview: latestMessage.body,
        attachmentCount: 0,
        openedCount: 0,
        clickCount: 0,
        originalSmsThread: thread.smsThread,
    };
}

export function mergeAndSortMessages(
    conversationFeed: ConversationFeedBasicsFragment | null | undefined,
): UnifiedMessage[] {
    const unified: UnifiedMessage[] = [];

    if (conversationFeed?.threads) {
        for (const thread of conversationFeed.threads) {
            if (
                thread.type === ConversationThreadType.EmailThread &&
                thread.emailThread
            ) {
                if (thread.emailThread.sentMessages?.[0]) {
                    unified.push(convertSentMessage(thread.emailThread.sentMessages[0]));
                }
                for (const msg of thread.emailThread.mailboxMessages ?? []) {
                    unified.push(convertReceivedMessage(msg));
                }
            } else if (thread.type === ConversationThreadType.Sms && thread.smsThread) {
                const smsMessage = convertSmsThread(thread);
                if (smsMessage) {
                    unified.push(smsMessage);
                }
            }
        }
    }

    unified.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return unified;
}
